import React, { useState, useEffect, useRef } from 'react';
import {
  Typography,
  Box,
  Button,
  Paper,
  Alert,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  CircularProgress,
  Chip,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FaDownload } from 'react-icons/fa';
import InterfaceHeader from '../components/InterfaceHeader';
import FavoriteButton from '../components/FavoriteButton';
import API_BASE_URL, { apiCall } from '../config/api';
import { TOOL_COLORS } from '../constants/colors';

const SOCIAL_MEDIA_ENDPOINTS = {
  PROCESS: `${API_BASE_URL}/api/SocialMediaContent/process`,
  STATUS: (processId) => `${API_BASE_URL}/api/SocialMediaContent/status/${processId}`,
  DOWNLOAD: (processId) => `${API_BASE_URL}/api/SocialMediaContent/download/${processId}`,
  SAMPLE: `${API_BASE_URL}/api/SocialMediaContent/sample`,
};

const SocialMediaContent = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const primaryColor = TOOL_COLORS.OTHER.primary;
  const primaryHover = TOOL_COLORS.OTHER.hover;
  const fileInputRef = useRef(null);
  const intervalRef = useRef(null);
  const processIdRef = useRef(null);
  const downloadTriggeredRef = useRef(false);

  const [file, setFile] = useState(null);
  const [productType, setProductType] = useState('rugs');
  const [processing, setProcessing] = useState(false);
  const [processId, setProcessId] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // Poll for status
  useEffect(() => {
    const currentStatus = status?.status || status?.Status;
    const isComplete = currentStatus === 'completed' || currentStatus === 'error';
    if (processId && (processing || (status && !isComplete))) {
      intervalRef.current = setInterval(() => fetchStatus(), 1000);
      return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
    }
  }, [processing, processId, status?.status, status?.Status]);

  useEffect(() => {
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) validateAndSetFile(selectedFile);
  };

  const validateAndSetFile = (selectedFile) => {
    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const ext = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();
    if (!validExtensions.includes(ext)) { setError('Please select a valid Excel (.xlsx, .xls) or CSV file'); return; }
    if (selectedFile.size > 50 * 1024 * 1024) { setError('File size must be less than 50MB'); return; }
    setFile(selectedFile);
    setError(null);
  };

  const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) validateAndSetFile(droppedFile);
  };

  const startProcessing = async () => {
    if (!file) { setError('Please select a file first'); return; }
    downloadTriggeredRef.current = false;
    setStatus(null); setProcessId(null); processIdRef.current = null;
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('productType', productType);

    try {
      setProcessing(true); setError(null); setSuccess(null);
      const data = await apiCall(SOCIAL_MEDIA_ENDPOINTS.PROCESS, { method: 'POST', body: formData });
      if (data.success) {
        setProcessId(data.processId);
        processIdRef.current = data.processId;
      } else {
        setError(data.message || 'Failed to start processing'); setProcessing(false);
      }
    } catch (err) { setError(`Error: ${err.message}`); setProcessing(false); }
  };

  const fetchStatus = async () => {
    const currentProcessId = processIdRef.current || processId;
    if (!currentProcessId) return;
    try {
      const data = await apiCall(SOCIAL_MEDIA_ENDPOINTS.STATUS(currentProcessId), { method: 'GET' });
      if (data.success) {
        const s = data.status;
        setStatus(s);
        const st = s.status ?? s.Status ?? '';
        if (st === 'completed' || st === 'error') {
          setProcessing(false);
          if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
          if (st === 'completed') {
            setSuccess('Social media content generation completed!');
            const awsUrl = s.awsUrl ?? s.AwsUrl ?? null;
            if (!downloadTriggeredRef.current) {
              downloadTriggeredRef.current = true;
              if (awsUrl) {
                const link = document.createElement('a');
                link.href = awsUrl;
                link.download = 'Social Media Content Result.xlsx';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.open(awsUrl, '_blank');
              } else {
                downloadResult(currentProcessId);
              }
            }
          } else { setError('Processing failed.'); }
        }
      }
    } catch (err) { console.error('Error fetching status:', err); }
  };

  const downloadResult = async (pId) => {
    const downloadProcessId = pId || processId;
    if (!downloadProcessId) return;
    try {
      setError(null);
      const response = await apiCall(`${SOCIAL_MEDIA_ENDPOINTS.DOWNLOAD(downloadProcessId)}?t=${Date.now()}`, {
        method: 'GET',
        headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate', 'Pragma': 'no-cache', 'Expires': '0' },
      });
      const blob = await response.blob();
      if (blob.size === 0) throw new Error('Downloaded file is empty');
      const nowResult = new Date().toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
      let filename = `Social Media Content Result ${nowResult.replace(/,/g, '').replace(/:/g, '-')}.xlsx`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none'; a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      setTimeout(() => { if (document.body.contains(a)) document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
    } catch (err) { setError(`Download failed: ${err.message}`); }
  };

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(SOCIAL_MEDIA_ENDPOINTS.SAMPLE, {
        method: 'GET',
        headers: { ...(token && { 'Authorization': `Bearer ${token}` }) },
      });
      if (!response.ok) throw new Error(`Download failed: ${response.status}`);
      const blob = await response.blob();
      const now = new Date().toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
      const filename = `Social Media Content Template ${now.replace(/,/g, '').replace(/:/g, '-')}.xlsx`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) { setError(`Template download failed: ${err.message}`); }
  };

  const clearFile = () => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; };
  const handleReset = () => {
    setFile(null); setProductType('rugs');
    setProcessing(false); setProcessId(null); processIdRef.current = null;
    setStatus(null); setError(null); setSuccess(null);
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const statusVal = status?.status ?? status?.Status ?? '';
  const progressPct = status?.progressPercentage ?? status?.ProgressPercentage ?? 0;
  const totalItems = status?.totalItems ?? status?.TotalItems ?? 0;
  const processedItems = status?.processedItems ?? status?.ProcessedItems ?? 0;
  const failedItems = status?.failedItems ?? status?.FailedItems ?? 0;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto', position: 'relative' }}>
      <FavoriteButton interfaceName="Social Media Content Generator" category="others" />
      <InterfaceHeader
        title="SOCIAL MEDIA CONTENT GENERATOR"
        color={primaryColor}
        category="others"
      />

      <Grid container spacing={3}>
        {/* Left Column - Upload & Config */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3, border: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: primaryColor }}>
              Upload & Process
            </Typography>

            {/* Product Category Toggle */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Product Category</Typography>
              <RadioGroup value={productType} onChange={(e) => setProductType(e.target.value)} row>
                <FormControlLabel value="rugs" control={<Radio sx={{ '&.Mui-checked': { color: primaryColor } }} />} label="Rugs" />
                <FormControlLabel value="furniture" control={<Radio sx={{ '&.Mui-checked': { color: primaryColor } }} />} label="Non-Rugs (Furniture)" />
              </RadioGroup>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* File Upload Zone */}
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Excel / CSV File
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Required column: ItemID
            </Typography>

            <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleFileSelect} style={{ display: 'none' }} />

            <Box
              onDragEnter={handleDragEnter} onDragLeave={handleDragLeave} onDragOver={handleDragOver} onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                border: `2px dashed ${isDragging ? primaryColor : file ? '#4CAF50' : theme.palette.divider}`,
                borderRadius: 2, p: 3, textAlign: 'center', cursor: 'pointer',
                bgcolor: isDragging
                  ? TOOL_COLORS.OTHER.light
                  : file
                    ? (isDarkMode ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.06)')
                    : 'transparent',
                transition: 'all 0.3s ease', mb: 2,
                '&:hover': {
                  borderColor: file ? '#4CAF50' : primaryColor,
                  bgcolor: file
                    ? (isDarkMode ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)')
                    : TOOL_COLORS.OTHER.light,
                },
              }}
            >
              {!file ? (
                <>
                  <CloudUploadIcon sx={{ fontSize: 48, color: isDragging ? primaryColor : 'text.secondary', mb: 1 }} />
                  <Typography variant="body1" gutterBottom>
                    {isDragging ? 'Drop file here' : 'Drop your Excel or CSV file here or click to browse'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Excel (.xlsx, .xls) or CSV (.csv) files supported
                  </Typography>
                </>
              ) : (
                <>
                  <CheckCircleIcon sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                  <Typography variant="body1" gutterBottom>1 file selected</Typography>
                  <Typography variant="caption" color="text.secondary">Click or drop to replace</Typography>
                </>
              )}
            </Box>

            {/* File Info Bar */}
            {file && (
              <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                p: 1, bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1, transition: 'all 0.2s ease', mb: 1,
                '&:hover': { bgcolor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)', borderColor: primaryColor },
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                  <DescriptionIcon sx={{ color: primaryColor, fontSize: 20 }} />
                  <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {file.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                    ({(file.size / 1024).toFixed(1)} KB)
                  </Typography>
                </Box>
                <Button size="small" onClick={(e) => { e.stopPropagation(); clearFile(); }}
                  sx={{ color: theme.palette.error.main, minWidth: 'auto', p: 0.5 }}>
                  <CloseIcon fontSize="small" />
                </Button>
              </Box>
            )}

            {/* Action Row */}
            <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, mt: 3, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={processing ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
                onClick={startProcessing}
                disabled={processing || !file}
                sx={{
                  flex: { xs: '1 1 100%', sm: 1 },
                  minWidth: { xs: '100%', sm: 200 },
                  py: 1.2, backgroundColor: primaryColor, color: 'white', textTransform: 'none', fontWeight: 600,
                  '&:hover': { backgroundColor: primaryHover },
                  '&:disabled': { backgroundColor: isDarkMode ? alpha('#fff', 0.12) : theme.palette.grey[300], color: isDarkMode ? alpha('#fff', 0.3) : theme.palette.grey[500] },
                }}
              >
                {processing ? 'Processing...' : 'PROCESS'}
              </Button>

              <Button
                variant="outlined"
                startIcon={<FaDownload />}
                onClick={downloadTemplate}
                sx={{
                  flex: { xs: '1 1 100%', sm: '0 0 auto' },
                  py: 1.2, textTransform: 'none', fontWeight: 600,
                  borderColor: primaryColor, color: primaryColor,
                  '&:hover': { borderColor: primaryHover, bgcolor: TOOL_COLORS.OTHER.light },
                }}
              >
                DOWNLOAD TEMPLATE
              </Button>
            </Box>
          </Paper>

          {/* Processing Status */}
          {status && (
            <Paper sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: primaryColor }}>Processing Status</Typography>
                <Chip
                  label={statusVal === 'completed' ? 'Completed' : statusVal === 'error' ? 'Error' : 'Processing...'}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    backgroundColor: statusVal === 'completed' ? alpha('#4caf50', 0.15) : statusVal === 'error' ? alpha('#f44336', 0.15) : alpha(primaryColor, 0.15),
                    color: statusVal === 'completed' ? '#4caf50' : statusVal === 'error' ? '#f44336' : primaryColor,
                  }}
                />
              </Box>

              <LinearProgress
                variant="determinate"
                value={progressPct}
                sx={{ mb: 2, height: 8, borderRadius: 4, backgroundColor: alpha(primaryColor, 0.15), '& .MuiLinearProgress-bar': { backgroundColor: primaryColor, borderRadius: 4 } }}
              />

              <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Progress: <strong>{processedItems}/{totalItems}</strong> items ({Math.round(progressPct)}%)
                </Typography>
                {failedItems > 0 && (
                  <Typography variant="body2" sx={{ color: '#f44336' }}>Failed: <strong>{failedItems}</strong></Typography>
                )}
              </Box>
            </Paper>
          )}
        </Grid>

        {/* Right Column - Instructions & Prompt */}
        <Grid item xs={12} md={4}>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

          <Paper sx={{ p: 3, mb: 2, border: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: primaryColor }}>Instructions</Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>1. Select product category (Rugs or Non-Rugs)</Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>2. Download the template and fill in ItemIDs</Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>3. Upload your file and click Process</Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>4. The tool generates captions for Instagram, Facebook, Pinterest, and TikTok</Typography>
            <Typography variant="body2">5. Results auto-download as Excel when complete</Typography>
          </Paper>

          <Paper sx={{ p: 3, mb: 2, border: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: primaryColor }}>How It Works</Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              The generator looks up each ItemID in the Items Detail database to pull product specifications (size, color, material, style, etc.).
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              It also matches each item to product images from the image links database. When an image is found, the AI analyzes it for visual details.
            </Typography>
            <Typography variant="body2">
              Powered by <strong>Google Gemini</strong> — generates platform-optimized captions tailored for each social media channel.
            </Typography>
          </Paper>

          {/* AI Prompt Dropdown */}
          <Paper sx={{ p: 3, mb: 2, border: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: primaryColor }}>AI Prompt</Typography>

            <Accordion sx={{ bgcolor: 'transparent', boxShadow: 'none', '&:before': { display: 'none' }, border: `1px solid ${theme.palette.divider}`, mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Social Media Content Prompt</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ fontFamily: 'monospace', fontSize: '0.7rem', lineHeight: 1.7, bgcolor: isDarkMode ? '#1a1a1a' : '#f5f5f5', borderRadius: 1, p: 2, whiteSpace: 'pre-wrap' }}>
{`You are an expert social media content creator for Safavieh, one of the largest home furnishing brands in the world. Generate platform-specific social media captions for the following product.

Product Specifications:
- Item ID: [itemId]
- Description: [description]
- Size: [size]
- Color: [color]
- Shape: [shape]
- Construction: [weave]
- Material: [content]
- Made in: [origin]
- Style: [style]
- Pattern: [pattern]

INSTAGRAM:
Write 2-3 engaging sentences that are visually descriptive and lifestyle-focused. End with exactly 15 relevant hashtags (include #Safavieh #HomeDecor and product-specific tags). Keep the tone aspirational and trendy.

FACEBOOK:
Write 3-4 sentences with a storytelling, lifestyle focus. Speak to homeowners looking to refresh their space. Warm, conversational tone. Include a subtle call-to-action like 'Shop now' or 'Discover more'.

PINTEREST:
Write an SEO-optimized, keyword-rich description (2-3 sentences). Focus on searchable terms like room type, style, color, and material. Make it pinnable and save-worthy. Include relevant keywords naturally.

TIKTOK:
Write a short, punchy hook (1 sentence) that grabs attention immediately, followed by a brief description and a clear CTA. Use a trendy, Gen-Z friendly tone. Keep it under 150 characters if possible.

STRICT RULES:
- No accented characters — use only standard ASCII English letters
- No references to any ethnicities or ethnic groups
- Write in a warm, confident tone appropriate for each platform
- Standard capitalization only`}
                </Box>
              </AccordionDetails>
            </Accordion>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              When a product image is found, the AI also analyzes visual details (colors, textures, patterns) from the image.
            </Typography>
          </Paper>

          <Paper sx={{ p: 3, mb: 2, border: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: primaryColor }}>Output Format</Typography>
            <Box sx={{ fontFamily: 'monospace', fontSize: '0.75rem', bgcolor: isDarkMode ? '#1a1a1a' : '#f0f0f0', borderRadius: 1, p: 1.5 }}>
              <Box sx={{ display: 'flex', gap: 2, fontWeight: 700, flexWrap: 'wrap' }}>
                <span>ItemID</span><span>Instagram</span><span>Facebook</span><span>Pinterest</span><span>TikTok</span><span>ImageUsed</span>
              </Box>
              <Divider sx={{ my: 0.5 }} />
              <Box sx={{ display: 'flex', gap: 2, color: 'text.secondary', flexWrap: 'wrap' }}>
                <span>CLS123-5</span><span>(caption)</span><span>(caption)</span><span>(caption)</span><span>(caption)</span><span>Yes</span>
              </Box>
            </Box>
          </Paper>

          <Paper sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: primaryColor }}>Template Format</Typography>
            <Box sx={{ fontFamily: 'monospace', fontSize: '0.75rem', bgcolor: isDarkMode ? '#1a1a1a' : '#f0f0f0', borderRadius: 1, p: 1.5 }}>
              <Box sx={{ display: 'flex', gap: 4, fontWeight: 700 }}>
                <span>ItemID</span>
              </Box>
              <Divider sx={{ my: 0.5 }} />
              <Box sx={{ display: 'flex', gap: 4, color: 'text.secondary' }}>
                <span>CLS123-5</span>
              </Box>
              <Box sx={{ display: 'flex', gap: 4, color: 'text.secondary' }}>
                <span>SGH456-8</span>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SocialMediaContent;
